import { createContext, useContext } from "react";

// What the steps need to know to give a typing user the screen: whether the
// keyboard is up, and whether the focus that raised it sits in a text field
// (a select or date picker raises none worth rearranging for).
export type KeyboardLayout = { open: boolean; typing: boolean };

const KeyboardLayoutContext = createContext<KeyboardLayout>({ open: false, typing: false });

export const KeyboardLayoutProvider = KeyboardLayoutContext.Provider;

export const useKeyboardLayout = () => useContext(KeyboardLayoutContext);

const TEXT_TYPES = new Set(["text", "search", "email", "tel", "url", "number", "password"]);

export const isTextField = (target: EventTarget | null): target is HTMLElement => {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  if (el.tagName === "TEXTAREA") return true;
  if (el.tagName !== "INPUT") return false;
  return TEXT_TYPES.has((el as HTMLInputElement).type || "text");
};

// Where the keyboard starts, in rect coordinates: the visual viewport's
// height. (WebKit folds its own keyboard pan into rects and reports it again
// as offsetTop, so offsetTop is deliberately not part of this.)
const viewBottom = () => {
  const vv = typeof window !== "undefined" ? window.visualViewport : null;
  return vv ? vv.height : window.innerHeight;
};

// Put a focused field where the keyboard can't cover it, inside the flow's
// own scroller. Browsers do this their own way or not at all — iOS Safari
// pans the whole page to centre the field (hiding whatever was above it),
// Chrome on iOS leaves a field in an inner scroller where it was — so the
// flow takes over: undo any page pan, pad the sheet by however much of the
// scroller now sits under the keyboard (so the last field can still come up),
// then scroll the field to the middle of what's visible. Fields that open a
// list below them ask for the top instead. Instant, not smooth: a field seen
// mid-animation somewhere else invites the browser to pan again.
export type SeatGeometry = {
  // How much of the scroller sits under the keyboard: the sheet pads by this.
  inset: number;
  // The scroller's visible height: a results list may grow into this.
  visible: number;
  // Where the field's top should be, in rect coordinates.
  wanted: number;
  // What to add to the scroller's scrollTop to put it there.
  delta: number;
};

export const seatGeometry = (
  field: { top: number; height: number },
  frame: { top: number; bottom: number },
  viewBottom: number,
  revealTop: boolean,
): SeatGeometry => {
  const bottom = Math.min(frame.bottom, viewBottom);
  const top = Math.max(frame.top, 0);
  const visible = Math.max(0, bottom - top);
  const wanted = top + (revealTop ? 12 : Math.max(12, (visible - field.height) / 2));
  return {
    inset: Math.max(0, Math.round(frame.bottom - bottom)),
    visible: Math.round(visible),
    wanted,
    delta: field.top - wanted,
  };
};

export const seatField = (field: HTMLElement, scroller: HTMLElement) => {
  if (!field.isConnected) return;
  if (window.scrollY !== 0) window.scrollTo(0, 0);
  const g = seatGeometry(field.getBoundingClientRect(), scroller.getBoundingClientRect(), viewBottom(), field.dataset.kbReveal === "top");
  scroller.style.setProperty("--pm-kb-inset", `${g.inset}px`);
  scroller.style.setProperty("--pm-kb-visible", `${g.visible}px`);
  if (Math.abs(g.delta) < 2) return;
  scroller.scrollTop += g.delta;
};

export const unseat = (scroller: HTMLElement) => {
  scroller.style.removeProperty("--pm-kb-inset");
  scroller.style.removeProperty("--pm-kb-visible");
};
